import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { MenuItem, Select } from '@mui/material'
import React from 'react'

export interface ReportTableColumn<R> {
    key: string
    label: string
    align?: 'right'
    minWidth?: number
    render: (row: R) => React.ReactNode
}

export interface ReportTableProps<R> {
    columns: ReportTableColumn<R>[]
    rows: R[]
    rowKey: (row: R) => string | number
    emptyText: string
    loading?: boolean
    page: number
    totalPages: number
    pages: number[]
    pageSize: number
    pageSizes: number[]
    rangeFrom: number
    rangeTo: number
    totalRows: number
    noun: string
    formatCount: (value: number) => string
    onPage: (page: number) => void
    onPageSize: (size: number) => void
    toolbar?: React.ReactNode
}

/** Tabla de reporte con encabezado fijo, desplazamiento horizontal y paginación en el navegador. */
const ReportTable = <R,>({
    columns,
    rows,
    rowKey,
    emptyText,
    loading = false,
    page,
    totalPages,
    pages,
    pageSize,
    pageSizes,
    rangeFrom,
    rangeTo,
    totalRows,
    noun,
    formatCount,
    onPage,
    onPageSize,
    toolbar,
}: ReportTableProps<R>) => (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
        {toolbar && <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-k-line px-4 py-3.5">{toolbar}</div>}

        <div className={`max-h-[640px] overflow-auto ${loading ? 'opacity-60' : ''}`}>
            <table className="w-max min-w-full border-collapse text-[13px]">
                <thead>
                    <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                        {columns.map((column, index) => (
                            <th
                                key={column.key}
                                className={`sticky top-0 z-10 whitespace-nowrap bg-k-surface px-2.5 py-3 font-bold ${index === 0 ? 'pl-4' : ''} ${column.align === 'right' ? 'text-right' : ''}`}
                                style={{ minWidth: column.minWidth }}
                            >
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-k-muted">{emptyText}</td></tr>
                    )}
                    {rows.map((row) => (
                        <tr key={rowKey(row)} className="border-t border-k-line hover:bg-k-hover">
                            {columns.map((column, index) => (
                                <td key={column.key} className={`max-w-[280px] px-2.5 py-2 ${index === 0 ? 'pl-4' : ''} ${column.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                                    {column.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line px-4 py-3 text-sm text-k-muted">
            <span className="hidden md:inline">Mostrando {formatCount(rangeFrom)} a {formatCount(rangeTo)} de {formatCount(totalRows)} {noun}</span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    aria-label="Página anterior"
                    disabled={page <= 1}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                    onClick={() => onPage(page - 1)}
                >
                    <ChevronLeftIcon fontSize="small" />
                </button>
                {pages.map((number) => (
                    <button
                        key={number}
                        type="button"
                        aria-current={number === page ? 'page' : undefined}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium ${number === page
                            ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                            : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                        onClick={() => onPage(number)}
                    >
                        {number}
                    </button>
                ))}
                <button
                    type="button"
                    aria-label="Página siguiente"
                    disabled={page >= totalPages}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                    onClick={() => onPage(page + 1)}
                >
                    <ChevronRightIcon fontSize="small" />
                </button>
                <Select<number>
                    size="small"
                    value={pageSize}
                    onChange={(event) => onPageSize(Number(event.target.value))}
                    inputProps={{ 'aria-label': 'Registros por página' }}
                    className="ml-1 h-8 min-w-[72px] rounded-md bg-k-surface text-sm text-k-ink [&_.MuiOutlinedInput-notchedOutline]:border-k-line [&_.MuiSelect-select]:min-h-0 [&_.MuiSelect-select]:py-0 [&_.MuiSelect-select]:leading-8 [&_.MuiSelect-select]:text-k-ink [&_.MuiSvgIcon-root]:text-k-muted"
                    MenuProps={{ slotProps: { paper: { className: 'v2-select-menu' } } }}
                >
                    {pageSizes.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                </Select>
            </div>
        </div>
    </section>
)

export default ReportTable
